import type {
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosRequestTransformer,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import axios, { AxiosError } from "axios";
import { isArray } from "lodash-es";

import { useClientStore } from "@/store/client";

export type HttpError<T = unknown> = AxiosError<T>;
export type HttpResponse<T = unknown> = AxiosResponse<T>;

export function makeApiClient(baseURL: string, options?: Partial<AxiosRequestConfig>) {
  return axios.create({
    baseURL,
    withCredentials: true,
    timeout: 10000,
    transformRequest,
    ...options,
  });
}

export function isHttpError<T = unknown>(error?: unknown): error is HttpError<T> {
  return error instanceof AxiosError || (error instanceof Object && "isAxiosError" in error);
}

export function isBadResponse(error: HttpError) {
  return error.code === AxiosError.ERR_BAD_RESPONSE;
}

export function isNetworkError(err: AxiosError) {
  return err?.code === AxiosError.ECONNABORTED || err?.code === AxiosError.ERR_NETWORK;
}

function transformRequest(
  this: InternalAxiosRequestConfig,
  data: unknown,
  headers: AxiosRequestHeaders,
) {
  if (headers && !headers["correlation-id"]) {
    headers["correlation-id"] = useClientStore().newCorrelationId();
  }
  for (const transform of defaultTransformers()) {
    data = transform.call(this, data, headers);
  }
  return data;
}

function defaultTransformers(): AxiosRequestTransformer[] {
  const transformers = axios.defaults.transformRequest;
  if (isArray(transformers)) {
    return transformers;
  }
  if (transformers) {
    return [transformers];
  }
  return [];
}
